import { useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  empresasPlataforma,
  type EmpresaPlataforma,
} from '@/mocks/backoffice/empresas';
import {
  getStatusColor,
  getStatusLabel,
  getPlanoLabel,
  updateEmpresa,
} from '@/mocks/backoffice/empresasHelpers';
import {
  participantesHistorico,
  receitaHistorico,
  statusParticipantes,
  segmentoEmpresas,
} from '@/mocks/backoffice/graficosEmpresas';
import EmpresaConfig from './components/EmpresaConfig';
import EmpresaUsuariosTab from './components/EmpresaUsuariosTab';

type TabId = 'visao' | 'editar' | 'financeiro' | 'relatorios' | 'historico' | 'configuracoes' | 'usuarios';

export default function BackofficeEmpresaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [empresa, setEmpresa] = useState<EmpresaPlataforma | undefined>(() =>
    empresasPlataforma.find((e) => e.id === id)
  );
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'config') return 'configuracoes';
    return 'visao';
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editForm, setEditForm] = useState<Partial<EmpresaPlataforma>>(() => {
    const e = empresasPlataforma.find((item) => item.id === id);
    return e ? { ...e } : {};
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [novoStatus, setNovoStatus] = useState<EmpresaPlataforma['status']>('ativa');

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  if (!empresa) {
    return (
      <BackofficeLayout>
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-gray-300"></i>
            </div>
          </div>
          <h2 className="text-lg font-bold text-gray-700">Empresa não encontrada</h2>
          <p className="text-sm text-gray-400 mt-2">O ID {id} não corresponde a nenhuma empresa cadastrada.</p>
          <Link
            to="/backoffice/empresas"
            className="mt-6 inline-flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            <span>Voltar para empresas</span>
          </Link>
        </div>
      </BackofficeLayout>
    );
  }

  const handleSave = () => {
    if (!id) return;
    const success = updateEmpresa(id, editForm);
    if (success) {
      const updated = empresasPlataforma.find((e) => e.id === id);
      if (updated) setEmpresa({ ...updated });
      mostrarToast('Dados da empresa atualizados com sucesso', 'success');
    }
  };

  const handleStatusChange = () => {
    if (!id) return;
    const success = updateEmpresa(id, { status: novoStatus });
    if (success) {
      const updated = empresasPlataforma.find((e) => e.id === id);
      if (updated) setEmpresa({ ...updated });
      mostrarToast(`Status alterado para ${getStatusLabel(novoStatus)}`, 'success');
      setShowStatusModal(false);
    }
  };

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'visao', label: 'Visão Geral', icon: 'ri-dashboard-line' },
    { id: 'relatorios', label: 'Relatórios', icon: 'ri-bar-chart-grouped-line' },
    { id: 'financeiro', label: 'Financeiro', icon: 'ri-money-dollar-circle-line' },
    { id: 'configuracoes', label: 'Configurações', icon: 'ri-settings-3-line' },
    { id: 'usuarios', label: 'Usuários', icon: 'ri-shield-user-line' },
    { id: 'editar', label: 'Editar Dados', icon: 'ri-edit-line' },
    { id: 'historico', label: 'Histórico', icon: 'ri-history-line' },
  ];

  // Mock charts data filtered to simulate this company's data
  const empresaParticipantes = participantesHistorico.map((p) => ({
    ...p,
    ativos: Math.round(p.ativos * (empresa.participantes_ativos / 1247)),
    total: Math.round(p.total * (empresa.participantes_total / 1500)),
    novos: Math.round(p.novos * (empresa.participantes_ativos / 1247)),
    churn: Math.round(p.churn * (empresa.participantes_ativos / 1247)),
  }));

  const empresaReceita = receitaHistorico.map((r) => ({
    ...r,
    receita: Math.round(r.receita * (empresa.faturamento_mensal / 12800)),
    meta: Math.round(r.meta * (empresa.faturamento_mensal / 12800)),
  }));

  const empresaStatus = [
    { name: 'Ativos', value: empresa.participantes_ativos, color: '#059669' },
    { name: 'Inativos', value: empresa.participantes_total - empresa.participantes_ativos, color: '#9ca3af' },
  ];

  const COLORS = ['#059669', '#9ca3af', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Link
              to="/backoffice/empresas"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-lg"></i>
              </div>
            </Link>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold text-gray-800">{empresa.nome_fantasia}</h1>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusColor(empresa.status)}`}>
                  {getStatusLabel(empresa.status)}
                </span>
              </div>
              <p className="text-sm text-gray-500">{empresa.razao_social} &middot; {empresa.cnpj}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setNovoStatus(empresa.status);
                setShowStatusModal(true);
              }}
              className="px-3 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Alterar Status
            </button>
            {activeTab !== 'editar' && (
              <button
                onClick={() => setActiveTab('editar')}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Editar Empresa
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-bold transition-all duration-200 cursor-pointer border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`}></i>
                  </div>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Visão Geral */}
            {activeTab === 'visao' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Participantes Ativos', value: empresa.participantes_ativos.toString(), sub: `de ${empresa.participantes_total} total`, icon: 'ri-user-line', color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Saldo Conta Digital', value: `R$ ${empresa.saldo_conta_digital.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, sub: `${empresa.pontos_disponiveis.toLocaleString('pt-BR')} pts disponíveis`, icon: 'ri-wallet-3-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Faturamento Mensal', value: `R$ ${empresa.faturamento_mensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, sub: getPlanoLabel(empresa.plano), icon: 'ri-bar-chart-box-line', color: 'text-amber-600', bg: 'bg-amber-50' },
                  ].map((card) => (
                    <div key={card.label} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center`}>
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${card.icon} ${card.color} text-lg`}></i>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">{card.label}</p>
                      </div>
                      <p className="text-xl font-bold text-gray-800">{card.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Informações da Empresa</h3>
                    <div className="space-y-3 text-sm">
                      {[
                        { label: 'Razão Social', value: empresa.razao_social },
                        { label: 'Nome Fantasia', value: empresa.nome_fantasia },
                        { label: 'CNPJ', value: empresa.cnpj },
                        { label: 'Email', value: empresa.email },
                        { label: 'Telefone', value: empresa.telefone },
                        { label: 'Segmento', value: empresa.segmento },
                        { label: 'Plano', value: getPlanoLabel(empresa.plano) },
                        { label: 'Data de Cadastro', value: new Date(empresa.data_cadastro).toLocaleDateString('pt-BR') },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-500">{item.label}</span>
                          <span className="font-medium text-gray-800">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Responsável</h3>
                    <div className="space-y-3 text-sm">
                      {[
                        { label: 'Nome', value: empresa.responsavel_nome },
                        { label: 'Email', value: empresa.responsavel_email },
                        { label: 'Cargo', value: empresa.responsavel_cargo },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-500">{item.label}</span>
                          <span className="font-medium text-gray-800">{item.value}</span>
                        </div>
                      ))}
                    </div>
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mt-6">Endereço</h3>
                    <div className="space-y-3 text-sm">
                      {[
                        { label: 'Endereço', value: empresa.endereco },
                        { label: 'Cidade', value: empresa.cidade },
                        { label: 'Estado', value: empresa.estado },
                        { label: 'CEP', value: empresa.cep },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-500">{item.label}</span>
                          <span className="font-medium text-gray-800">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Relatórios / Gráficos */}
            {activeTab === 'relatorios' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Participantes ao longo do tempo */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="text-sm font-bold text-gray-700 mb-1">Participantes ao Longo do Tempo</h3>
                    <p className="text-xs text-gray-400 mb-4">Evolução de participantes ativos, novos e churn</p>
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={empresaParticipantes} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorAtivos" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorNovos" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.1} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="ativos" name="Ativos" stroke="#3b82f6" fill="url(#colorAtivos)" strokeWidth={2} />
                        <Area type="monotone" dataKey="novos" name="Novos" stroke="#059669" fill="url(#colorNovos)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Receita vs Meta */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="text-sm font-bold text-gray-700 mb-1">Receita vs Meta</h3>
                    <p className="text-xs text-gray-400 mb-4">Comparativo mensal de faturamento</p>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={empresaReceita} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `R${v/1000}k`} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                          formatter={(value: number) => [`R$ ${value.toLocaleString('pt-BR')}`, '']}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="receita" name="Receita" fill="#059669" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="meta" name="Meta" fill="#e5e7eb" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Status dos Participantes */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="text-sm font-bold text-gray-700 mb-1">Status dos Participantes</h3>
                    <p className="text-xs text-gray-400 mb-4">Distribuição ativos vs inativos</p>
                    <div className="flex items-center justify-center">
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            data={empresaStatus}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={4}
                            dataKey="value"
                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                            labelStyle={{ fontSize: 12 }}
                          >
                            {empresaStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center space-x-6 mt-2">
                      {empresaStatus.map((s) => (
                        <div key={s.name} className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
                          <span className="text-xs text-gray-600">{s.name}: <span className="font-bold">{s.value}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Segmentos */}
                  <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h3 className="text-sm font-bold text-gray-700 mb-1">Segmentos de Empresas (Plataforma)</h3>
                    <p className="text-xs text-gray-400 mb-4">Distribuição por segmento de mercado</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={segmentoEmpresas} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={90} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="value" name="Empresas" radius={[0, 4, 4, 0]}>
                          {segmentoEmpresas.map((_entry, index) => (
                            <Cell key={`seg-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Financeiro */}
            {activeTab === 'financeiro' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Saldo Conta Digital</p>
                    <p className="text-xl font-bold text-emerald-700">
                      R$ {empresa.saldo_conta_digital.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Pontos Disponíveis</p>
                    <p className="text-xl font-bold text-blue-700">
                      {empresa.pontos_disponiveis.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Faturamento Mensal</p>
                    <p className="text-xl font-bold text-amber-700">
                      R$ {empresa.faturamento_mensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-700 mb-4">Evolução da Receita (Últimos 6 meses)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={empresaReceita} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `R${v/1000}k`} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        formatter={(value: number) => [`R$ ${value.toLocaleString('pt-BR')}`, '']}
                      />
                      <Area type="monotone" dataKey="receita" name="Receita" stroke="#059669" fill="url(#colorReceita)" strokeWidth={2} />
                      <Area type="monotone" dataKey="meta" name="Meta" stroke="#9ca3af" fill="none" strokeWidth={2} strokeDasharray="5 5" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Configurações */}
            {activeTab === 'configuracoes' && (
              <EmpresaConfig empresa={empresa} />
            )}

            {/* Usuários */}
            {activeTab === 'usuarios' && (
              <EmpresaUsuariosTab empresaId={empresa.id} empresaNome={empresa.nome_fantasia} />
            )}

            {/* Editar Dados */}
            {activeTab === 'editar' && (
              <div className="space-y-6 max-w-3xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Razão Social</label>
                    <input
                      type="text"
                      value={editForm.razao_social || ''}
                      onChange={(e) => setEditForm({ ...editForm, razao_social: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Nome Fantasia</label>
                    <input
                      type="text"
                      value={editForm.nome_fantasia || ''}
                      onChange={(e) => setEditForm({ ...editForm, nome_fantasia: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">CNPJ</label>
                    <input
                      type="text"
                      value={editForm.cnpj || ''}
                      onChange={(e) => setEditForm({ ...editForm, cnpj: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Email</label>
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Telefone</label>
                    <input
                      type="text"
                      value={editForm.telefone || ''}
                      onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Segmento</label>
                    <input
                      type="text"
                      value={editForm.segmento || ''}
                      onChange={(e) => setEditForm({ ...editForm, segmento: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Plano</label>
                    <select
                      value={editForm.plano || 'basico'}
                      onChange={(e) => setEditForm({ ...editForm, plano: e.target.value as EmpresaPlataforma['plano'] })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
                    >
                      <option value="basico">Básico</option>
                      <option value="profissional">Profissional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Responsável - Nome</label>
                    <input
                      type="text"
                      value={editForm.responsavel_nome || ''}
                      onChange={(e) => setEditForm({ ...editForm, responsavel_nome: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Responsável - Email</label>
                    <input
                      type="email"
                      value={editForm.responsavel_email || ''}
                      onChange={(e) => setEditForm({ ...editForm, responsavel_email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Responsável - Cargo</label>
                    <input
                      type="text"
                      value={editForm.responsavel_cargo || ''}
                      onChange={(e) => setEditForm({ ...editForm, responsavel_cargo: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Endereço</label>
                    <input
                      type="text"
                      value={editForm.endereco || ''}
                      onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Cidade</label>
                    <input
                      type="text"
                      value={editForm.cidade || ''}
                      onChange={(e) => setEditForm({ ...editForm, cidade: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Estado</label>
                    <input
                      type="text"
                      value={editForm.estado || ''}
                      onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">CEP</label>
                    <input
                      type="text"
                      value={editForm.cep || ''}
                      onChange={(e) => setEditForm({ ...editForm, cep: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Salvar Alterações
                  </button>
                  <button
                    onClick={() => {
                      const e = empresasPlataforma.find((item) => item.id === id);
                      if (e) setEditForm({ ...e });
                    }}
                    className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Histórico */}
            {activeTab === 'historico' && (
              <div className="space-y-4">
                {[
                  { acao: 'Cadastro realizado', data: empresa.data_cadastro, autor: 'Sistema', tipo: 'info' },
                  empresa.ultimo_acesso && { acao: 'Último acesso na plataforma', data: new Date(empresa.ultimo_acesso).toLocaleDateString('pt-BR'), autor: empresa.responsavel_nome, tipo: 'info' },
                  { acao: `Status atual: ${getStatusLabel(empresa.status)}`, data: new Date().toLocaleDateString('pt-BR'), autor: 'Sistema', tipo: empresa.status === 'ativa' ? 'success' : 'warning' },
                ].filter(Boolean).map((evt, i) => (
                  <div key={i} className="flex items-start space-x-3 py-3 border-b border-gray-50">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      (evt as { tipo: string }).tipo === 'success' ? 'bg-emerald-50' : (evt as { tipo: string }).tipo === 'warning' ? 'bg-amber-50' : 'bg-blue-50'
                    }`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${
                          (evt as { tipo: string }).tipo === 'success' ? 'ri-check-line text-emerald-600' :
                          (evt as { tipo: string }).tipo === 'warning' ? 'ri-alert-line text-amber-600' :
                          'ri-information-line text-blue-600'
                        } text-xs`}></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-800">{(evt as { acao: string }).acao}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {(evt as { autor: string }).autor} &middot; {(evt as { data: string }).data}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Status Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm space-y-4">
              <h3 className="text-lg font-bold text-gray-800">Alterar Status</h3>
              <p className="text-sm text-gray-500">Selecione o novo status da empresa:</p>
              <div className="space-y-2">
                {(['ativa', 'pendente', 'suspensa', 'inativa'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setNovoStatus(s)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                      novoStatus === s ? 'bg-emerald-50 border border-emerald-200' : 'border border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      s === 'ativa' ? 'bg-emerald-500' : s === 'pendente' ? 'bg-amber-500' : s === 'suspensa' ? 'bg-orange-500' : 'bg-gray-400'
                    }`}></span>
                    <span className={`font-bold ${novoStatus === s ? 'text-emerald-700' : 'text-gray-700'}`}>{getStatusLabel(s)}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center space-x-3 pt-2">
                <button
                  onClick={handleStatusChange}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Confirmar
                </button>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 ${
            toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-${toast.type === 'success' ? 'check-line' : 'error-warning-line'} text-sm`}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
}